import WavesurferPlayer from '@wavesurfer/react';
import classNames from 'classnames';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { DownloadIcon, PauseIcon, PlayIcon, SoundIcon, SoundMute1Icon } from 'tdesign-icons-react';
import { Button, Popup, Slider } from 'tdesign-react';
import type WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { RegionParams } from 'wavesurfer.js/dist/plugins/regions.esm.js';

import { PRIMARY_COLOR } from '@/const/style';
import { downloadFileUrl } from '@/utils/downloadFile';

import { registerWsPlayer, stopPlayer } from '../../core/player/utils/stopPlay';
import { updateName } from '../../image/utils/updateName';
import styles from './index.module.less';
import { bufferToWave, formatTime } from './util';

interface AudioEditorRef {
  crop: () => void;
}

interface AudioEditorProps {
  url: string;
  name?: string;
  volume?: number;
  height?: number;
  isEdit?: boolean;
  visible?: boolean;
  disabled?: boolean;
  onCrop?: (blob: Blob | null, name: string) => void;
  onVolumeChange?: (volume: number) => void;
  controlClassName?: string;
  controlStyle?: {
    playIcon?: {
      size: number,
    }
    time?: React.CSSProperties
  }
}

const AudioEditor = forwardRef<AudioEditorRef, AudioEditorProps>(({
  url,
  name,
  height,
  isEdit = false,
  visible,
  disabled = false,
  controlClassName,
  controlStyle,
  onCrop,
  onVolumeChange,
  ...props
}, ref) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
  const regionRef = useRef<RegionParams | null>(null);
  const regions = useMemo(() => RegionsPlugin.create(), []);

  const [isPlaying, setIsPlaying] = useState<Boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(100);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isEdit && !audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }, []);

  useEffect(() => {
    if (!visible) {
      regions.clearRegions();
      wavesurfer?.stop();
    }
  }, [visible]);

  useEffect(() => {
    if (props.volume !== undefined) {
      setVolume(Number(props.volume));
    } else {
      setVolume(100);
    }
  }, [props.volume]);

  useEffect(() => {
    if (url) {
      setLoading(true);
    } else {
      setLoading(false);
    }

    return () => {
      setLoading(false);
    };
  }, [url]);

  useEffect(() => {
    if (wavesurfer) {
      return registerWsPlayer(wavesurfer);
    }
  }, [wavesurfer]);

  const onReady = (ws: WaveSurfer) => {
    setLoading(false);
    setWavesurfer(ws);
    setIsPlaying(false);
    setDuration(ws.getDuration());
    ws.setVolume(volume / 100);

    if (isEdit) {
      regions.clearRegions();
      fetch(url)
        .then(response => response.arrayBuffer())
        .then(arrayBuffer => audioContextRef.current?.decodeAudioData(arrayBuffer))
        .then(buffer => audioBufferRef.current = buffer || null)
        .catch((error) => {
          console.error('Error with decoding audio data', error);
        });;
    }
  };

  useEffect(() => {
    if (isEdit && wavesurfer) {
      wavesurfer.registerPlugin(regions);

      regions.on('region-created', (reg) => {
        regions.getRegions().forEach((r) => {
          if (r.id !== reg.id) {
            r.remove();
          }
        });
        regionRef.current = reg;
      });

      regions.on('region-updated', (reg) => {
        regionRef.current = reg;
      });

      regions.on('region-clicked', (_, e) => {
        e.stopPropagation();
        playRegion();
      });

      regions.enableDragSelection({
        color: 'rgba(10, 123, 255, 0.1)',
      });
    }
  }, [wavesurfer]);

  useImperativeHandle(ref, () => ({
    crop: () => {
      if (!isEdit || disabled) return;
      wavesurfer?.stop();
      const audioBlob = handleClipRegion();
      if (audioBlob) {
        onCrop?.(audioBlob, updateName(name));
        setCurrentTime(0);
      } else {
        onCrop?.(null, '');
      }
    },
  }));

  const playRegion = () => {
    const audioBuffer = audioBufferRef.current;
    const region = regionRef.current;
    if (audioBuffer && region) {
      const endTime = region.end ?? audioBuffer.duration;
      const handler = function (current: number) {
        if (current > endTime) {
          wavesurfer?.pause();
          wavesurfer?.un('timeupdate', handler);
        }
      };
      wavesurfer?.setTime(region.start);
      wavesurfer?.on('timeupdate', handler);
      wavesurfer?.play();
    }
  };

  const handleClipRegion = () => {
    const audioBuffer = audioBufferRef.current;
    const region = regionRef.current;
    if (audioBuffer && region) {
      const startTime = region.start;
      const endTime = region.end ?? audioBuffer.duration;
      const startSample = Math.round(startTime * audioBuffer.sampleRate);
      const endSample = Math.round(endTime * audioBuffer.sampleRate);
      const frameCount = endSample - startSample;
      const clipBuffer = audioContextRef.current!.createBuffer(
        audioBuffer.numberOfChannels,
        frameCount,
        audioBuffer.sampleRate,
      );

      for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
        const clipData = clipBuffer?.getChannelData(channel);
        const originalData = audioBuffer.getChannelData(channel);
        clipData.set(originalData.subarray(startSample, endSample));
      }

      const blob = bufferToWave(clipBuffer);
      return blob;
    }
  };

  const handlePlayPause = async () => {
    if (wavesurfer?.isPlaying()) {
      wavesurfer.pause();
      return;
    }

    await stopPlayer();
    wavesurfer?.play();
  };

  const onInteraction = () => {
    setCurrentTime(wavesurfer?.getCurrentTime() || 0);
    if (isEdit) {
      handlePlayPause();
    }
  };

  const handleVolumeChange = (val: number) => {
    setVolume(val);
    wavesurfer?.setVolume(val / 100);
    onVolumeChange?.(val);
  };

  const handleDownload = async () => {
    if (url) {
      downloadFileUrl(name || '未命名.mp3', url);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div style={{ width: '100%', height: '100%' }}>
        <WavesurferPlayer
          url={url}
          height={height}
          waveColor='#313131'
          progressColor={PRIMARY_COLOR}
          plugins={useMemo(() => [RegionsPlugin.create()], [])}
          onReady={onReady}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onAudioprocess={() => setCurrentTime(wavesurfer?.getCurrentTime() || 0)}
          onInteraction={onInteraction}
        />
      </div>
      <div className={classNames(styles.control, controlClassName)} >
        <div className={styles.left}>
          <Button theme='default' shape='circle' variant='text'
            style={{ width: 24, height: 24 }}
            icon={isPlaying ? <PauseIcon size={controlStyle?.playIcon?.size || 16} /> : <PlayIcon size={controlStyle?.playIcon?.size || 16} />}
            loading={loading}
            onClick={handlePlayPause}
          />
          <div className={styles.time} style={controlStyle?.time}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
        <div className={styles.right}>
          <Popup trigger='click'
            content={
              <div
                style={{ width: '100%', height: '100%' }}
                onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}>
                <Slider
                  layout='vertical' min={0} max={100}
                  value={volume}
                  onChange={val => handleVolumeChange(val as number)}
                />
              </div>}
            overlayStyle={{ marginTop: '-6px' }}
            overlayInnerStyle={{ width: 24, height: 100, padding: '12px 0', borderRadius: 12 }}
          >
            <Button theme='default' shape='circle' variant='text' disabled={disabled}
              style={{ width: 24, height: 24 }}
              icon={volume ? <SoundIcon size={14} /> : <SoundMute1Icon size={14} />}
              onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
            />
          </Popup>
          <Button theme='default' shape='circle' variant='text'
            style={{ width: 24, height: 24 }}
            icon={<DownloadIcon size={14} />}
            onClick={handleDownload}
          />
        </div>
      </div>
    </div>
  );
});

export default AudioEditor;
